import type { AdminJob } from '@ecowheels/types';
import { useQuery } from '@tanstack/react-query';
import { NavLink, Outlet } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from './ui';
import { StatsBar } from './stats-bar';

const NAV = [
  { to: '/jobs', label: 'Munkafelvételek' },
  { to: '/posts', label: 'Bejegyzések' },
  { to: '/vehicles', label: 'Használt autók' },
  { to: '/gallery', label: 'Galéria' },
  { to: '/newsletter', label: 'Hírlevél' },
];

/**
 * Every job left in GET /admin/jobs is one the workshop hasn't been notified
 * about yet (see jobs.tsx) — so its length is the retry-queue size. The owner
 * decided this badge, not an email alert, is the notification: a queue that
 * only a page nobody opens can see is exactly how the 2026-07-08 outage went
 * unnoticed for three weeks.
 */
function useFailedJobsCount() {
  const { data } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => api.get<AdminJob[]>('/admin/jobs'),
    refetchInterval: 60_000,
  });
  return data?.length ?? 0;
}

export function Layout() {
  const { logout } = useAuth();
  const failedJobsCount = useFailedJobsCount();

  return (
    <div className="min-h-screen">
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <span className="text-sm font-semibold uppercase tracking-wide text-brand">
            EcoWheels Admin
          </span>
          <nav>
            <ul className="flex flex-wrap gap-1">
              {NAV.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `rounded-md px-3 py-1.5 text-sm transition-colors ${
                        isActive ? 'bg-brand/15 text-brand' : 'text-white/70 hover:text-white'
                      }`
                    }
                  >
                    {item.label}
                    {item.to === '/jobs' && failedJobsCount > 0 && (
                      <span className="ml-1.5 rounded-full bg-red-500/90 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                        {failedJobsCount}
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
          <Button variant="ghost" onClick={logout}>
            Kijelentkezés
          </Button>
        </div>
      </header>

      <StatsBar />

      <main className="mx-auto max-w-6xl px-4 py-8">
        <Outlet />
      </main>
    </div>
  );
}
